import { PDFDocument, rgb } from "pdf-lib";
import QRCode from "qrcode";
import { type SigningMetadata, type SignatureEntry } from "./signing-metadata";
import { loadPdfTheme } from "./pdf-theme";
import { generateIntroPage } from "./pdf-intro";
import { renderFieldToImage, type FilledField } from "./pdf-utils";
import { generateMetaPage } from "./meta-page";

export const SIGNING_METADATA_FILENAME = "signing-metadata.json";

/**
 * Deterministically reconstructs the final presentation PDF solely from the pristine
 * original payload and the cryptographically valid metadata mappings. 
 * This prevents Shadow Attacks by completely discarding any attacker modifications
 * to the outer PDF container before displaying/downloading the validated version.
 */
export async function reconstructDocument(
    originalPdfBuffer: ArrayBuffer,
    metadata: SigningMetadata,
    docTitle?: string
): Promise<Uint8Array> {
    const pdfDoc = await PDFDocument.load(originalPdfBuffer.slice(0));

    // 1. Are we fully signed?
    const totalSigners = metadata.signers.length;
    const uniqueSigners = new Set(metadata.signatures.map((s) => s.email));
    const isComplete = uniqueSigners.size >= totalSigners;

    const theme = await loadPdfTheme(pdfDoc);

    // 2. Insert Intro Page if not fully completed
    if (!isComplete) {
        await generateIntroPage(pdfDoc, {
            isComplete: false,
            documentTitle: docTitle || "Untitled Document",
            existingMetadata: metadata,
            theme,
        });
    }

    // 3. Aggregate all field data from all signatures
    const allFilled: Record<string, FilledField> = {};
    metadata.signatures.forEach((sig) => {
        if (sig.sessionInput) {
            Object.assign(allFilled, sig.sessionInput);
        }
    });

    // 4. Render fields onto clean pages based on JSON metadata coordinates
    const { helvetica } = theme;
    const pages = pdfDoc.getPages();
    const introInserted = !isComplete;

    const renderFieldToImageLocal = (filled: FilledField, pxW: number, pxH: number, opts?: any) =>
        renderFieldToImage(pdfDoc, filled, pxW, pxH, opts);

    for (const field of metadata.fields || []) {
        // If field.page is absolute to original document content (1-based index)
        const fieldPageShim = field.page;
        const pageIdx = fieldPageShim - 1 + (introInserted ? 1 : 0);
        const page = pages[pageIdx];
        if (!page) continue;

        const x = field.x * (72 / 25.4);
        const y = page.getSize().height - field.y * (72 / 25.4) - field.height * (72 / 25.4);
        const pxW = field.width * (72 / 25.4);
        const pxH = field.height * (72 / 25.4);

        if (field.type === "label") {
            page.drawText(field.labelText || "", { x: x + 2, y: y + 2, size: 9 });
            continue;
        }

        const filled = allFilled[field.id];

        if (filled) {
            if (
                filled.type === "name" ||
                filled.type === "date" ||
                filled.type === "date-now" ||
                filled.type === "text"
            ) {
                page.drawRectangle({ x, y, width: pxW, height: pxH, color: rgb(1, 1, 1) });
                page.drawText(filled.value, { x: x + 4, y: y + 4, size: 10, font: helvetica });
            } else if (filled.type === "checkbox") {
                page.drawRectangle({
                    x, y, width: pxH, height: pxH,
                    color: rgb(1, 1, 1),
                    borderColor: rgb(0, 0, 0),
                    borderWidth: 1,
                });
                if (filled.value === "true") {
                    page.drawText("X", { x: x + 2, y: y + 2, size: pxH - 4, font: helvetica });
                }
            } else if (filled.type === "signature" || filled.type === "initials") {
                // Signature frame box
                const sigboxsvg = `<svg width="100%" height="100%" viewBox="0 0 418 146" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" xmlns:serif="http://www.serif.com/" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:1.5;">
    <g transform="matrix(1,0,0,1,-1417.564259,-2659.177153)">
        <path d="M1757.596,2661.26L1821.369,2661.26C1827.715,2661.26 1832.867,2666.412 1832.867,2672.758L1832.867,2791.139C1832.867,2797.485 1827.715,2802.637 1821.369,2802.637L1574.605,2802.637" style="fill:none;stroke:black;stroke-width:4.17px;"/>
        <path d="M1444.638,2802.637L1431.145,2802.637C1424.799,2802.637 1419.648,2797.485 1419.648,2791.139L1419.648,2672.758C1419.648,2666.412 1424.799,2661.26 1431.145,2661.26L1632.062,2661.26" style="fill:none;stroke:black;stroke-width:4.17px;"/>
    </g>
</svg>`;
                const frameImg = await renderFieldToImageLocal(
                    { fieldId: "frame", type: "signature", value: sigboxsvg } as any,
                    pxW, pxH
                );
                page.drawImage(frameImg, { x, y, width: pxW, height: pxH });

                const sigEntry = metadata.signatures.find(s => s.sessionInput?.[field.id]);

                if (sigEntry) {
                    if (filled.type === "signature") {
                        const qrSize = pxH - 8;
                        const sigWidth = pxW - qrSize - 15;

                        if (sigWidth > 0) {
                            const sigImg = await renderFieldToImageLocal(filled, sigWidth, pxH - 8, { contain: true });
                            page.drawImage(sigImg, { x: x + 6, y: y + 4, width: sigWidth, height: pxH - 8 });
                        }

                        const idText = `ID: ${sigEntry.keyId.slice(0, 8)}`;
                        const idX = x + pxW * 0.22 - 22;
                        page.drawRectangle({ x: idX, y: y - 1, width: 45, height: 6, color: rgb(1, 1, 1) });
                        page.drawText(idText, { x: idX + 3, y, size: 4, font: helvetica });

                        const spX = x + pxW * 0.7 - 20;
                        page.drawRectangle({ x: spX, y: y + pxH - 1, width: 40, height: 6, color: rgb(1, 1, 1) });
                        page.drawText("Signed+", { x: spX + 5, y: y + pxH + 0.5, size: 4, font: helvetica });

                        const qrData = `Signed+\ndoc:${metadata.documentHash.slice(0, 12)}\nsig:${sigEntry.signature.slice(0, 12)}`;
                        const qrImage = await pdfDoc.embedPng(
                            await QRCode.toDataURL(qrData, { margin: 1, width: 80 })
                        );
                        const qrX = x + pxW - qrSize - 4;
                        page.drawImage(qrImage, { x: qrX, y: y + 4, width: qrSize, height: qrSize });
                    } else {
                        const sigImg = await renderFieldToImageLocal(filled, pxW - 8, pxH - 4, { contain: true });
                        page.drawImage(sigImg, { x: x + 4, y: y + 2, width: pxW - 8, height: pxH - 4 });
                    }
                }
            }
        } else if (!isComplete) {
            // Draw placeholder box for unfilled fields if document is awaiting signatures
            page.drawRectangle({
                x, y, width: pxW, height: pxH,
                color: rgb(0.95, 0.95, 0.95),
                borderColor: rgb(0.2, 0.2, 0.2),
                borderWidth: 1,
            });
            const text = field.type.toUpperCase();
            page.drawText(text, {
                x: x + 2,
                y: y + pxH / 2 - 5,
                size: 10,
                color: rgb(0.2, 0.2, 0.2),
            });
        }
    }

    // 5. Append Meta Page
    await generateMetaPage(
        pdfDoc,
        {
            documentHash: metadata.documentHash,
            isLastSigner: isComplete,
            existingMetadata: metadata,
            updatedSignatures: metadata.signatures,
        },
        {
            pdfDoc,
            ...theme,
            renderFieldToImage: renderFieldToImageLocal,
        }
    );

    // 6. Embed Original Metadata
    const finalMetaBytes = new TextEncoder().encode(JSON.stringify(metadata, null, 2));
    await pdfDoc.attach(finalMetaBytes, SIGNING_METADATA_FILENAME, {
        mimeType: "application/json",
        description: "Signing metadata",
    });

    await pdfDoc.attach(originalPdfBuffer.slice(0), "original.pdf", {
        mimeType: "application/pdf",
        description: "Original document",
    });

    return await pdfDoc.save();
}
